export const AUTH_ACCOUNT_TYPES = Object.freeze({
  ATTORNEY: "attorney",
  ATTORNEY_STAFF: "attorney_staff",
});

export const FIRM_SETTING_PERMISSION_KEYS = Object.freeze([
  "assign_firm_user",
  "view_all_cases",
  "view_only_team_cases",
  "communicate_via_email",
  "communicate_via_chat",
  "communicate_via_text",
  "modify_firm_branding",
  "modify_firm_avatar",
  "edit_notes",
  "edit_statute_of_limitations",
  "edit_litigation_events",
  "receive_non_client_scanned_email",
  "edit_letter_templates",
  "edit_discovery_objections",
  "add_new_client",
  "edit_todo_permission",
]);

export const ACCOUNT_PERMISSION_KEYS = Object.freeze([
  "reset_password_permission",
  "accounting_admin_permission",
  "accounting_permission",
  "isIntake",
]);

export const AUTH_PERMISSION_KEYS = Object.freeze([
  ...FIRM_SETTING_PERMISSION_KEYS,
  ...ACCOUNT_PERMISSION_KEYS,
]);

const AUTH_PERMISSION_KEY_SET = new Set(AUTH_PERMISSION_KEYS);

export const normalizePermissionUserId = (value) => {
  if (value === undefined || value === null) return null;

  const normalizedValue = String(value).trim();
  return normalizedValue === "" ? null : normalizedValue;
};

export const createEmptyPermissions = () =>
  Object.fromEntries(AUTH_PERMISSION_KEYS.map((key) => [key, false]));

export const createInitialAuthPermissionState = () => ({
  accountType: null,
  initialized: false,
  hasFullAccess: false,
  subjectUserId: null,
  permissions: createEmptyPermissions(),
});

const getLoginSubjectUserId = (loginData, accountData) =>
  normalizePermissionUserId(loginData?.user?.id ?? accountData?.user?.id);

export const normalizeLoginPermissions = (loginData = {}) => {
  if (loginData?.attorney) {
    return {
      accountType: AUTH_ACCOUNT_TYPES.ATTORNEY,
      initialized: true,
      hasFullAccess: true,
      subjectUserId: getLoginSubjectUserId(loginData, loginData.attorney),
      permissions: createEmptyPermissions(),
    };
  }

  if (loginData?.attorney_staff) {
    const attorneyStaff = loginData.attorney_staff;
    const firmSettings = attorneyStaff.for_firmsetting || {};
    const permissions = createEmptyPermissions();

    FIRM_SETTING_PERMISSION_KEYS.forEach((key) => {
      permissions[key] = firmSettings[key] === true;
    });
    ACCOUNT_PERMISSION_KEYS.forEach((key) => {
      permissions[key] = attorneyStaff[key] === true;
    });

    return {
      accountType: AUTH_ACCOUNT_TYPES.ATTORNEY_STAFF,
      initialized: true,
      hasFullAccess: false,
      subjectUserId: getLoginSubjectUserId(loginData, attorneyStaff),
      permissions,
    };
  }

  return createInitialAuthPermissionState();
};

export const hasAuthPermission = (permissionState, permissionKey) => {
  if (typeof permissionKey !== "string" || permissionKey.trim() === "") return false;
  if (!permissionState?.initialized) return false;
  if (permissionState.hasFullAccess === true) return true;

  return permissionState.permissions?.[permissionKey] === true;
};

export const setAuthPermissionSubject = (permissionState, userId) => {
  const subjectUserId = normalizePermissionUserId(userId);
  if (!subjectUserId) return permissionState;

  return {
    ...permissionState,
    subjectUserId,
  };
};

export const applyAuthPermissionPatchForUser = (permissionState, { userId, permissions } = {}) => {
  const targetUserId = normalizePermissionUserId(userId);
  const subjectUserId = normalizePermissionUserId(permissionState?.subjectUserId);

  if (
    !permissionState?.initialized ||
    permissionState.accountType !== AUTH_ACCOUNT_TYPES.ATTORNEY_STAFF ||
    !targetUserId ||
    !subjectUserId ||
    targetUserId !== subjectUserId ||
    !permissions ||
    typeof permissions !== "object"
  ) {
    return permissionState;
  }

  const acceptedEntries = Object.entries(permissions).filter(
    ([key, value]) => AUTH_PERMISSION_KEY_SET.has(key) && typeof value === "boolean"
  );
  if (acceptedEntries.length === 0) return permissionState;

  return {
    ...permissionState,
    permissions: {
      ...permissionState.permissions,
      ...Object.fromEntries(acceptedEntries),
    },
  };
};
